import { type ReactNode, useState } from 'react'

import {
  KnowledgeBaseDocumentIcon,
  KnowledgeBaseSourceTag,
} from '@/components/knowledge-base/KnowledgeBaseDocumentIcon'
import {
  KNOWLEDGE_BASE_SOURCES,
  type KnowledgeBaseDocument,
  type KnowledgeBaseSource,
} from '@/lib/definitions/knowledgeBase'
import {
  findKnowledgeBaseDocument,
  knowledgeBaseSourceOf,
  parseKnowledgeBaseKey,
} from '@/lib/misc/knowledgeBase'
import { cn } from '@/lib/misc/Utils'
import { useKnowledgeBase } from '@/lib/provider/KnowledgeBaseProvider'
import { useKnowledgeBaseCatalogue } from '@/lib/queries/KnowledgeBase.queries'

import ChevronRightIcon from '@icons/chevron-right'
import ClockIcon from '@icons/clock'

type Scope = 'all' | KnowledgeBaseSource

const scopesOf = (
  documents: KnowledgeBaseDocument[]
): { id: Scope; label: string }[] => {
  const sources = (
    Object.keys(KNOWLEDGE_BASE_SOURCES) as KnowledgeBaseSource[]
  ).filter((source) =>
    documents.some(
      (document) => knowledgeBaseSourceOf(document.path) === source
    )
  )
  if (sources.length < 2) return []
  return [
    { id: 'all', label: 'All docs' },
    ...sources.map((id) => ({ id, label: KNOWLEDGE_BASE_SOURCES[id].label })),
  ]
}

const SectionTitle = ({ children }: { children: ReactNode }) => (
  <p className="mx-0.5 mt-6 mb-2.5 flex items-center gap-2 text-[11px] font-semibold tracking-wider text-neutral-45 uppercase">
    {children}
  </p>
)

const KnowledgeBaseHome = () => {
  const { navigate, recent, suggestions } = useKnowledgeBase()
  const [scope, setScope] = useState<Scope>('all')
  const { documents: catalogue, isLoading } = useKnowledgeBaseCatalogue()
  const scopes = scopesOf(catalogue)
  const activeScope = scopes.some(({ id }) => id === scope) ? scope : 'all'

  const suggested = suggestions.flatMap((key) => {
    const document = findKnowledgeBaseDocument(
      catalogue,
      parseKnowledgeBaseKey(key).path
    )
    return document ? [{ key, document }] : []
  })
  const recentDocuments = recent.flatMap((path) => {
    const document = findKnowledgeBaseDocument(catalogue, path)
    return document ? [document] : []
  })
  const groups = catalogue
    .filter(
      (document) =>
        activeScope === 'all' ||
        knowledgeBaseSourceOf(document.path) === activeScope
    )
    .reduce<{ category: string; documents: KnowledgeBaseDocument[] }[]>(
      (result, document) => {
        const group = result.find(
          ({ category }) => category === document.category.label
        )
        if (group) group.documents.push(document)
        else
          result.push({
            category: document.category.label,
            documents: [document],
          })
        return result
      },
      []
    )

  return (
    <div className="px-4 pt-14.5 pb-7">
      <div className="relative overflow-hidden rounded-2xl bg-radial-[at_100%_0%] from-accent-60 via-brand-1 to-auxiliary-3 px-4.5 pt-4.5 pb-5 text-white">
        <span className="pointer-events-none absolute -top-10 -right-10 size-40 rounded-full bg-radial from-brand-3/35 to-transparent to-65%" />
        <h2 className="text-[18px] font-semibold tracking-tightest">
          How can we help?
        </h2>
        <p className="mt-1.5 max-w-[34ch] text-[13px] leading-normal text-accent-20">
          Guides, product specs and API docs for everything you run on
          Inter.link.
        </p>
      </div>

      {suggested.length > 0 && (
        <>
          <SectionTitle>Suggested for this page</SectionTitle>
          <div className="flex flex-col gap-1.5">
            {suggested.map(({ key, document }) => (
              <button
                key={key}
                type="button"
                onClick={() => navigate(key)}
                className="group flex items-center gap-3 rounded-xl border border-hairline px-3 py-2.5 text-left transition-all hover:translate-x-0.5 hover:border-accent-30 hover:bg-accent-05"
              >
                <KnowledgeBaseDocumentIcon document={document} />
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5 text-[13.5px] font-semibold">
                    {document.title}
                    <KnowledgeBaseSourceTag path={document.path} />
                  </span>
                  <span className="block truncate text-[12.5px] text-neutral-50">
                    {document.description}
                  </span>
                </span>
                <ChevronRightIcon className="ml-auto size-4 shrink-0 text-neutral-40" />
              </button>
            ))}
          </div>
        </>
      )}

      {recentDocuments.length > 0 && (
        <>
          <SectionTitle>Recently viewed</SectionTitle>
          <div className="flex flex-wrap gap-1.5">
            {recentDocuments.map((document) => (
              <button
                key={document.path}
                type="button"
                onClick={() => navigate(document.path)}
                className="inline-flex items-center gap-1.5 rounded-full border border-hairline px-2.5 py-1 text-[12.5px] transition-colors hover:border-accent-30 hover:text-accent-100"
              >
                <ClockIcon className="size-3.5" />
                {document.title}
              </button>
            ))}
          </div>
        </>
      )}

      {scopes.length > 0 && (
        <div
          role="tablist"
          aria-label="Documentation source"
          className="mt-5 inline-flex gap-0.5 rounded-[10px] bg-neutral-10 p-[3px]"
        >
          {scopes.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={activeScope === id}
              onClick={() => setScope(id)}
              className={cn(
                'rounded-lg px-3 py-1 text-[12.5px] font-medium transition-colors',
                activeScope === id
                  ? 'bg-surface text-text-base shadow-btn'
                  : 'text-neutral-50 hover:text-text-base'
              )}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {isLoading && groups.length === 0 && (
        <p className="mt-6 text-[13px] text-neutral-50">
          Loading documentation…
        </p>
      )}

      {groups.map(({ category, documents }) => (
        <section key={category}>
          <SectionTitle>{category}</SectionTitle>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(190px,1fr))] gap-1">
            {documents.map((document) => (
              <button
                key={document.path}
                type="button"
                onClick={() => navigate(document.path)}
                className="flex items-center gap-2.5 rounded-xl p-2.5 text-left transition-colors hover:bg-neutral-10"
              >
                <KnowledgeBaseDocumentIcon document={document} />
                <span className="min-w-0">
                  <span className="block text-[13px] leading-snug font-medium">
                    {document.title}
                  </span>
                  <span className="block truncate text-[11.5px] text-neutral-50">
                    {document.description}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}

export default KnowledgeBaseHome
